import axios, { AxiosError } from 'axios';
import type { UserJwtToken } from 'src/types/common.types';
import customStorage from 'src/utils/storage';
import utils from 'src/utils/utils';

const MarketPublicClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
});

const MarketPrivateClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
});

export const getAccessToken = () => {
  const userString = customStorage.getItem('user');
  const user = utils.jsonParse(typeof userString === 'string' ? userString : null) as UserJwtToken;
  if (!user) throw new Error('No Session');
  if (!user.expired) throw new Error('No expired');
  if (new Date(user.expired) < new Date()) throw new Error('Expired');
  return user.access_token;
};

MarketPrivateClient.interceptors.request.use((config) => {
  if (!config.headers) return config;
  config.headers.Authorization = `Bearer ${getAccessToken()}`;
  return config;
});

MarketPrivateClient.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    if (error instanceof AxiosError) {
      const status = error && error.response && error.response.data && error.response.data.status;
      if (status === '2009') {
        if (window.location.pathname === '/account/logout') return;

        const retUrl = `${window.location.pathname}${window.location.search}`;
        window.location.href = `/account/logout?retUrl=${encodeURIComponent(retUrl)}`;
        return await Promise.reject(error);
      }
    }
    return await Promise.reject(error);
  },
);

export const axiosInstance = {
  market: {
    public: MarketPublicClient,
    private: MarketPrivateClient,
  },
};

/**
 * @description
 * AxiosError에서 status를 가져온다.
 * 만약 AxiosError가 아니면 null을 반환한다.
 * @param error {unknown}
 * @returns {string | null}
 * @example
 * const errorStatus = getAxiosErrorStatus(error);
 * switch (errorStatus) {
 *  case '2009':
 *   alert('로그아웃되었습니다.');
 *   break;
 *  default:
 *   alert('알 수 없는 에러가 발생했습니다.');
 *   break;
 * }
 */
export const getAxiosErrorStatus = (error: unknown): string | null => {
  if (error instanceof AxiosError) {
    if (error.response) {
      const errorStatus = error.response.data.status;
      return errorStatus;
    }
  }
  return null;
};

export const getAxiosErrorMessage = (error: unknown): string => {
  const defaultErrorMessage = '잠시 후 다시 시도해주세요.';

  if (error instanceof AxiosError) {
    if (error.response) {
      const errorMessage = error.response?.data?.message;

      return errorMessage ?? defaultErrorMessage;
    }
  }

  return defaultErrorMessage;
};

/**
 * @description
 * AxiosError에서 data를 가져온다.
 * 만약 AxiosError가 아니면 null을 반환한다.
 * @param error {unknown}
 * @returns {Record<string, unknown> | null}
 */
export const getAxiosErrorData = (error: unknown): Record<string, unknown> | null => {
  if (error instanceof AxiosError) {
    if (error.response) {
      const errorData = error.response?.data?.data;
      return errorData ?? null;
    }
  }
  return null;
};

/**
 *
 * @param requestUrl {string}
 * @description
 * queryString을 requestUrl에 붙여서 반환한다.
 * 만약 queryString이 없으면 requestUrl을 그대로 반환한다.
 * @returns
 */
export const withQueryString = (requestUrl: string): string => {
  if (typeof window === 'undefined') return requestUrl;

  const rawParams = new URLSearchParams(window.location.search);
  const queryPairs: string[] = [];

  rawParams.forEach((value, key) => {
    queryPairs.push(`${key}=${value}`);
  });

  if (queryPairs.length === 0) return requestUrl;

  const queryString = queryPairs.join('&');

  // 이미 requestUrl에 ? 가 포함되어 있는지 확인
  const connector = requestUrl.includes('?') ? '&' : '?';

  return `${requestUrl}${connector}${queryString}`;
};
